<template>
  <v-select
    ref="selector"
    :value="computedValue"
    :name="name"
    :disabled="disabled"
    :clearable="clearable"
    :filterable="filterable"
    :multiple="multiple"
    :options="options"
    :no-drop="noDrop"
    :placeholder="placeholder"
    :push-tags="pushTags"
    :taggable="taggable"
    :reduce="reduce"
    class="selector"
    @search:focus="$emit('search-focus')"
    @option:created="onOptionCreated"
    @input="onInput"
  >
    <template slot="selected-option" slot-scope="option">
      <SelectorOption :label="option.label" :image-url="option.imageUrl" />
    </template>

    <template slot="option" slot-scope="option">
      <SelectorOption :label="option.label" :image-url="option.imageUrl" />
    </template>

    <template #search="{attributes, events}">
      <input
        ref="search"
        class="vs__search"
        :required="required && !computedValue"
        v-bind="attributes"
        v-on="events"
      />
    </template>
  </v-select>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import SelectorOption from "@/components/UI/SelectorOption";

export default {
  name: "Selector",
  components: { SelectorOption, vSelect },
  props: {
    clearable: {
      default: false,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    loading: {
      default: false,
      type: Boolean,
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    name: {
      required: true,
      type: String,
    },
    noDrop: {
      type: Boolean,
      default: false,
    },
    options: {
      default: () => [],
      required: false,
      type: Array,
    },
    placeholder: {
      type: String,
      default: "",
    },
    pushTags: {
      type: Boolean,
      default: false,
    },
    reduce: {
      type: Function,
      default: (option) => option,
    },
    required: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    value: {
      default: () => {},
      type: Object,
    },
  },
  data: function() {
    return {
      currentValue: this.value,
      selected: null,
    };
  },
  computed: {
    computedValue() {
      return this.loading ? "Loading..." : this.currentValue;
    },
  },
  methods: {
    onInput(value) {
      this.$emit("input", value);
      this.currentValue = value;
    },
    onOptionCreated() {
      if (this.taggable && this.multiple) {
        // Get the search input...
        const search = this.$get(this, "$refs.search");

        // Once the DOM re-renders focus the search input...
        this.$nextTick(() => {
          search.focus();
        });
      }
    },
  },
};
</script>

<style scoped>
>>> .dropdown-menu {
  z-index: 1000;
}
</style>

<style>
.selector .vs__search::placeholder,
.selector .vs__dropdown-toggle,
.selector .vs__dropdown-menu {
  @apply border border-grey-light rounded-lg w-full text-support leading-normal bg-white;
}

.selector.vs--disabled {
  opacity: 0.6;

  .vs__search {
    @apply bg-transparent;
  }
}

.selector .vs__dropdown-toggle {
  @apply shadow;
}

.selector .vs__dropdown-toggle {
  height: 49px;
  @apply overflow-hidden;
}

.selector .vs__selected-options {
  @apply px-0;
}

.selector:not(.vs--single) .vs__selected {
  @apply bg-brand-light text-brand text-sm;
  margin: 8px 0 4px 6px;

  &:hover {
    @apply bg-brand text-white border-brand-dark cursor-default;
    text-shadow: none;

    .vs__deselect {
      @apply opacity-100;
      fill: #ffffff;
      text-shadow: none;
    }
  }
}

.selector .vs--single .vs__selected {
  @apply text-base;
  margin: 4px 2px 0;
}

.selector .vs__deselect {
  @apply opacity-50 relative;
  fill: #eb485f;
  top: 1px;
}

.selector.vs--open .vs__dropdown-toggle {
  @apply border border-grey-light rounded-b-none;
}
.selector.vs--open .vs__selected {
  top: 6px;
}

.selector .vs__dropdown-menu {
  @apply rounded-t-none p-0;
}

.selector .vs__dropdown-option {
  @apply text-support text-base px-2 py-3 leading-none;
  margin: 4px 0 0;
}

.selector .vs__dropdown-option--highlight {
  @apply bg-brand text-white;
}

.selector .vs__no-options {
  @apply text-left px-2 pb-3 m-0;
}

.selector .vs__open-indicator {
  @apply cursor-pointer;
}

.selector .vs__actions {
  @apply relative;
  background: rgba(255, 255, 255, 0.83);
  right: 5px;
  z-index: 1;
}
</style>
